import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetUsers } from "../../hooks/users/useGetUsers";
import { useDeleteUser } from "../../hooks/users/useDeleteUser";
import { useDeleteAllUsers } from "../../hooks/users/useDeleteAllUsers";
import { useRestoreAllUsers } from "../../hooks/users/useRestoreAllUsers";
import { useForceDeleteAllUsers } from "../../hooks/users/useForceDeleteAllUsers";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import UsersTableRow from "./Table/UsersTableRow";
import { UsersTableColumns } from "./Table/UsersTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";
import { useAuthContext } from "../../auth/useAuthContext";

const UsersTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();
    const { user: currentUser } = useAuthContext();

    const perms = useMemo(
        () => ({
            list: has("viewAny-user"),
            create: has("create-user"),
            edit: has("update-user"),
            delete: has("delete-user"),
        }),
        [has]
    );

    const navigate = useNavigate();

    // Filter States
    const [trashedFilter, setTrashedFilter] = useState("");

    // CRUD Hooks
    const { deleteUserMutate, isPending: isDelete } = useDeleteUser();
    const { deleteAllUsersMutate, isPending: isDeleteAll } = useDeleteAllUsers();
    const { restoreAllUsersMutate, isPending: isRestoreAll } = useRestoreAllUsers();
    const { forceDeleteAllUsersMutate, isPending: isForceDeleteAll } = useForceDeleteAllUsers();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        selectedItem,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/users",
        deleteSingle: deleteUserMutate,
        deleteAll: deleteAllUsersMutate,
        restoreSingle: (id, options) => restoreAllUsersMutate({ items: [id] }, options),
        restoreAll: restoreAllUsersMutate,
        forceDeleteSingle: (id, options) => forceDeleteAllUsersMutate({ items: [id] }, options),
        forceDeleteAll: forceDeleteAllUsersMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            sort_by: 'created_at',
            sort_order: order,
        }),
        [page, perPage, search, trashedFilter, order]
    );

    const { isLoading, error, users, meta } = useGetUsers(params);

    // تجهيز الفلاتر لـ TableLayout
    // No additional filters for users currently besides archive
    const filters = [];

    const columns = useMemo(() => UsersTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "المستخدمون", en: "Users" }}
            columns={columns}
            items={users}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={filters}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, users)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/users/create")}
            addButtonLabel={{ ar: "إضافة مستخدم", en: "Add User" }}
            page={page}
            perPage={perPage}
            order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            menuDisableDelete={selectedItem?.id === currentUser?.id}
            menuDisableRestore={selectedItem?.id === currentUser?.id}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <UsersTableRow
                    {...params}
                    key={params.item.id}
                    user={params.item}
                    perms={perms}
                    onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default UsersTable;